import type {
  CategoryBreakdown,
  ScoreCategoryKey,
  ScoreResult,
  ScoringInput,
} from './types';

// ===========================================================================
// Tax bracket lookup — flat effective rates, single vs married.
// ===========================================================================

interface TaxBracket {
  upTo: number;
  single: number;
  married: number;
}

const TOP_TAX_BRACKET: TaxBracket = {
  upTo: Infinity,
  single: 0.35,
  married: 0.32,
};

const TAX_BRACKETS: TaxBracket[] = [
  {upTo: 50_000, single: 0.12, married: 0.1},
  {upTo: 100_000, single: 0.17, married: 0.14},
  {upTo: 150_000, single: 0.22, married: 0.18},
  {upTo: 200_000, single: 0.24, married: 0.22},
  {upTo: 300_000, single: 0.27, married: 0.24},
  {upTo: 500_000, single: 0.3, married: 0.28},
  TOP_TAX_BRACKET,
];

// `upTo` is the exclusive upper bound of each bracket — a bracket covers
// `income < upTo`, with the next bracket picking up at the boundary. This
// matches the source-of-truth spreadsheet's bracket labels (`$50k–$99k`,
// `$200k–$299k`, etc.), where the lower number is inclusive and the upper
// is exclusive of the next bracket's start.
const effectiveTaxRate = (income: number, married: boolean): number => {
  const bracket = TAX_BRACKETS.find((b) => income < b.upTo) ?? TOP_TAX_BRACKET;

  return married ? bracket.married : bracket.single;
};

// ===========================================================================
// Age-banded scaled weights (already include the redistribution of the
// un-scored Portfolio Quality + Tax Efficiency weight).
// ===========================================================================

type WeightBandKey =
  | 'u35_noKids'
  | 'u35_kids'
  | 'a35_49_noKids'
  | 'a35_49_kids'
  | 'a50_59'
  | 'a60';

const WEIGHTS: Record<ScoreCategoryKey, Record<WeightBandKey, number>> = {
  savingsRate: {
    u35_noKids: 0.3,
    u35_kids: 0.28,
    a35_49_noKids: 0.27,
    a35_49_kids: 0.23,
    a50_59: 0.19,
    a60: 0.15,
  },
  dti: {
    u35_noKids: 0.23,
    u35_kids: 0.22,
    a35_49_noKids: 0.18,
    a35_49_kids: 0.17,
    a50_59: 0.18,
    a60: 0.17,
  },
  emergencyFund: {
    u35_noKids: 0.22,
    u35_kids: 0.21,
    a35_49_noKids: 0.18,
    a35_49_kids: 0.17,
    a50_59: 0.16,
    a60: 0.15,
  },
  netWorthRatio: {
    u35_noKids: 0.13,
    u35_kids: 0.13,
    a35_49_noKids: 0.18,
    a35_49_kids: 0.18,
    a50_59: 0.24,
    a60: 0.26,
  },
  estatePlanning: {
    u35_noKids: 0.06,
    u35_kids: 0.06,
    a35_49_noKids: 0.09,
    a35_49_kids: 0.09,
    a50_59: 0.12,
    a60: 0.19,
  },
  insurance: {
    u35_noKids: 0.06,
    u35_kids: 0.1,
    a35_49_noKids: 0.1,
    a35_49_kids: 0.16,
    a50_59: 0.11,
    a60: 0.08,
  },
};

const weightBand = (age: number, hasChildren: boolean): WeightBandKey => {
  if (age >= 60) return 'a60';
  if (age >= 50) return 'a50_59';
  if (age >= 35) return hasChildren ? 'a35_49_kids' : 'a35_49_noKids';

  return hasChildren ? 'u35_kids' : 'u35_noKids';
};

const weightFor = (
  category: ScoreCategoryKey,
  age: number,
  hasChildren: boolean,
): number => WEIGHTS[category][weightBand(age, hasChildren)];

// ===========================================================================
// Category scorers — each returns a number 0–100.
// ===========================================================================

const scoreSavingsRate = (
  input: ScoringInput,
  afterTax: number,
  savingsRate: number,
): number => {
  const {
    age,
    annualIncome,
    liquidSavings,
    nonRetirementInvestments,
    retirementAccounts,
    monthlyExpenses,
  } = input;

  if (annualIncome <= 0) return 0;

  if (age >= 65) {
    // Working/saving branch: positive savings rate => use a relaxed ladder.
    if (savingsRate > 0) {
      if (savingsRate >= 0.05) return 100;
      if (savingsRate >= 0.03) return 80;
      if (savingsRate >= 0.01) return 60;

      return 40;
    }

    // Drawdown branch: runway = liquid+investments+retirement / (annual expenses - after-tax income)
    const drawableAssets =
      liquidSavings + nonRetirementInvestments + retirementAccounts;

    if (drawableAssets <= 0) return 20;

    const annualDrawdown = monthlyExpenses * 12 - afterTax;

    if (annualDrawdown <= 0) return 40;

    const runway = drawableAssets / annualDrawdown;

    if (runway >= 25) return 100;
    if (runway >= 20) return 90;
    if (runway >= 15) return 80;
    if (runway >= 10) return 60;
    if (runway >= 5) return 40;

    return 20;
  }

  // Under 65: age-banded thresholds. Each band has the same shape:
  // [100, 80, 60, 40, 20, 0] at decreasing rate cutoffs.
  const bands: {minAge: number; cutoffs: [number, number, number, number]}[] = [
    {minAge: 55, cutoffs: [0.22, 0.15, 0.08, 0.03]},
    {minAge: 45, cutoffs: [0.18, 0.12, 0.06, 0.02]},
    {minAge: 35, cutoffs: [0.14, 0.1, 0.05, 0.02]},
    {minAge: 0, cutoffs: [0.08, 0.05, 0.03, 0.01]},
  ];
  const band = bands.find((b) => age >= b.minAge);

  if (!band) return 0;

  const [c100, c80, c60, c40] = band.cutoffs;

  if (savingsRate >= c100) return 100;
  if (savingsRate >= c80) return 80;
  if (savingsRate >= c60) return 60;
  if (savingsRate >= c40) return 40;
  if (savingsRate > 0) return 20;

  return 0;
};

const scoreDti = (dti: number, annualIncome: number): number => {
  if (annualIncome <= 0) return 0;
  if (dti <= 0.1) return 100;
  if (dti <= 0.2) return 80;
  if (dti <= 0.3) return 60;
  if (dti <= 0.4) return 40;
  if (dti <= 0.5) return 20;

  return 0;
};

const scoreEmergencyFund = (
  months: number,
  monthlyExpenses: number,
): number => {
  if (monthlyExpenses === 0) return 100;
  if (months >= 6) return 100;
  if (months >= 3) return 75;
  if (months >= 1) return 40;

  return 10;
};

const scoreNetWorthRatio = (ratio: number, annualIncome: number): number => {
  if (annualIncome <= 0) return 0;
  if (ratio >= 3) return 100;
  if (ratio >= 2) return 80;
  if (ratio >= 1) return 60;
  if (ratio >= 0.5) return 40;

  return 20;
};

const estateBaseScore = (docs: number): number => {
  if (docs >= 5) return 95;
  if (docs === 4) return 85;
  if (docs === 3) return 70;
  if (docs === 2) return 55;
  if (docs === 1) return 40;

  return 15;
};

const scoreEstatePlanning = (input: ScoringInput, netWorth: number): number => {
  const {age, married, hasChildren, estateDocsCount} = input;
  const base = estateBaseScore(estateDocsCount);
  // Penalty grows with "need" (age, family, wealth) and is blunted by docs.
  let penalty = 0;

  if (age >= 60) penalty += 10;
  else if (age >= 50) penalty += 5;
  if (married) penalty += 5;
  if (hasChildren) penalty += 5;
  if (netWorth > 1_000_000) penalty += 5;
  else if (netWorth > 500_000) penalty += 3;

  const scaled = Math.round(penalty * (1 - Math.min(estateDocsCount, 5) / 5));

  return Math.max(10, Math.min(100, base - scaled));
};

const insuranceBaseUnder65 = (cnt: number): number => {
  if (cnt >= 4) return 95;
  if (cnt === 3) return 80;
  if (cnt === 2) return 65;
  if (cnt === 1) return 45;

  return 15;
};

const insuranceBase65Plus = (cnt: number): number => {
  if (cnt >= 2) return 100;
  if (cnt === 1) return 80;

  return 50;
};

const scoreInsurance = (input: ScoringInput): number => {
  const {age, married, hasChildren, annualIncome, totalDebt, insuranceCount} =
    input;

  if (age >= 65) return insuranceBase65Plus(insuranceCount);

  const base = insuranceBaseUnder65(insuranceCount);
  let penalty = 0;

  if (age >= 50) penalty += 5;
  if (married) penalty += 5;
  if (hasChildren) penalty += 8;
  if (annualIncome > 200_000) penalty += 5;
  else if (annualIncome > 100_000) penalty += 3;
  if (totalDebt > annualIncome) penalty += 5;

  const scaled = Math.round(penalty * (1 - Math.min(insuranceCount, 4) / 4));

  return Math.max(10, Math.min(100, base - scaled));
};

// ===========================================================================
// Orchestrator
// ===========================================================================

export const calculateScore = (input: ScoringInput): ScoreResult => {
  const {
    age,
    married,
    hasChildren,
    annualIncome,
    monthlyExpenses,
    monthlyDebtPayments,
    liquidSavings,
    nonRetirementInvestments,
    retirementAccounts,
    realEstateValue,
    totalDebt,
  } = input;

  // Intermediate values
  const afterTaxIncome =
    annualIncome > 0
      ? annualIncome * (1 - effectiveTaxRate(annualIncome, married))
      : 0;
  const annualExpenses = monthlyExpenses * 12;
  const savingsRate =
    afterTaxIncome > 0 ? (afterTaxIncome - annualExpenses) / afterTaxIncome : 0;
  const dti = annualIncome > 0 ? monthlyDebtPayments / (annualIncome / 12) : 0;
  const emergencyFundMonths =
    monthlyExpenses > 0 ? liquidSavings / monthlyExpenses : 0;
  const netWorth =
    liquidSavings +
    nonRetirementInvestments +
    retirementAccounts +
    realEstateValue -
    totalDebt;
  const netWorthRatio = annualIncome > 0 ? netWorth / annualIncome : 0;

  // Per-category raw scores
  const raw: Record<ScoreCategoryKey, number> = {
    savingsRate: scoreSavingsRate(input, afterTaxIncome, savingsRate),
    dti: scoreDti(dti, annualIncome),
    emergencyFund: scoreEmergencyFund(emergencyFundMonths, monthlyExpenses),
    netWorthRatio: scoreNetWorthRatio(netWorthRatio, annualIncome),
    estatePlanning: scoreEstatePlanning(input, netWorth),
    insurance: scoreInsurance(input),
  };

  const getWeight = (category: ScoreCategoryKey): number =>
    weightFor(category, age, hasChildren);

  const breakdown: CategoryBreakdown[] = [
    {
      key: 'savingsRate',
      label: 'Savings Rate',
      rawScore: raw.savingsRate,
      weight: getWeight('savingsRate'),
      weighted: raw.savingsRate * getWeight('savingsRate'),
      notes: `Savings rate ${(savingsRate * 100).toFixed(1)}%`,
    },
    {
      key: 'dti',
      label: 'Debt-to-Income',
      rawScore: raw.dti,
      weight: getWeight('dti'),
      weighted: raw.dti * getWeight('dti'),
      notes: `DTI ${(dti * 100).toFixed(1)}%`,
    },
    {
      key: 'emergencyFund',
      label: 'Emergency Fund',
      rawScore: raw.emergencyFund,
      weight: getWeight('emergencyFund'),
      weighted: raw.emergencyFund * getWeight('emergencyFund'),
      notes: `${emergencyFundMonths.toFixed(1)} months`,
    },
    {
      key: 'netWorthRatio',
      label: 'Net Worth Ratio',
      rawScore: raw.netWorthRatio,
      weight: getWeight('netWorthRatio'),
      weighted: raw.netWorthRatio * getWeight('netWorthRatio'),
      notes: `Net worth ${netWorthRatio.toFixed(2)}x income`,
    },
    {
      key: 'estatePlanning',
      label: 'Estate Planning',
      rawScore: raw.estatePlanning,
      weight: getWeight('estatePlanning'),
      weighted: raw.estatePlanning * getWeight('estatePlanning'),
      notes: `${input.estateDocsCount} docs`,
    },
    {
      key: 'insurance',
      label: 'Insurance',
      rawScore: raw.insurance,
      weight: getWeight('insurance'),
      weighted: raw.insurance * getWeight('insurance'),
      notes: `${input.insuranceCount} coverage types`,
    },
  ];

  const finalScore = Math.max(
    0,
    Math.min(
      100,
      Math.round(breakdown.reduce((sum, b) => sum + b.weighted, 0)),
    ),
  );

  return {
    finalScore,
    afterTaxIncome,
    investableAssets: liquidSavings + nonRetirementInvestments,
    savingsRatePercent: savingsRate * 100,
    dtiPercent: dti * 100,
    emergencyFundMonths,
    netWorth,
    netWorthRatio,
    breakdown,
  };
};
